import React, { useEffect, useState } from "react";
import { Container, Row, Col, Table, Button, Modal, Form, Spinner, Badge } from "react-bootstrap";
import { Pencil, Plus, X } from "react-bootstrap-icons";
import UserService from "../../services/user.service";
import LogoService from "../../services/logo.service";
import CatalogService from "../../services/catalog.service";

const AdminCapabilities = () => {
  const [capabilities, setCapabilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCapability, setSelectedCapability] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [logos, setLogos] = useState([]);
  const [colors, setColors] = useState([]);
  const [capabilityLogos, setCapabilityLogos] = useState([]);
  const [capabilityColors, setCapabilityColors] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [capsResponse, logosResponse, colorsResponse] = await Promise.all([
        UserService.getProductCapabilities(),
        LogoService.getLogos(),
        CatalogService.getColors()
      ]);
      setCapabilities(capsResponse);
      setLogos(logosResponse.data || logosResponse);
      setColors(colorsResponse.data || colorsResponse);
    } catch (error) {
      console.error("Error loading data:", error);
    }
    setLoading(false);
  };

  const openModal = async (capability) => {
    setSelectedCapability(capability);
    setShowModal(true);
    try {
      const details = await UserService.getCapabilityDetails(capability.id);
      setCapabilityLogos(details.capability_logos?.map(cl => cl.decorationLogoId) || []);
      setCapabilityColors(details.capability_colors?.map(cc => cc.colorId) || []);
    } catch (error) {
      console.error("Error loading capability details:", error);
      setCapabilityLogos([]);
      setCapabilityColors([]);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedCapability(null);
    setCapabilityLogos([]);
    setCapabilityColors([]);
  };

  const toggleLogo = async (logoId) => {
    setSaving(true);
    const isSelected = capabilityLogos.includes(logoId);
    try {
      await UserService.setCapabilityLogo(selectedCapability.id, logoId, !isSelected);
      if (isSelected) {
        setCapabilityLogos(capabilityLogos.filter(id => id !== logoId));
      } else {
        setCapabilityLogos([...capabilityLogos, logoId]);
      }
    } catch (error) {
      console.error("Error toggling logo:", error);
    }
    setSaving(false);
  };

  const toggleColor = async (colorId) => {
    setSaving(true);
    const isSelected = capabilityColors.includes(colorId);
    try {
      await UserService.setCapabilityColor(selectedCapability.id, colorId, !isSelected);
      if (isSelected) {
        setCapabilityColors(capabilityColors.filter(id => id !== colorId));
      } else {
        setCapabilityColors([...capabilityColors, colorId]);
      }
    } catch (error) {
      console.error("Error toggling color:", error);
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <Container>
        <Spinner animation="border" />
      </Container>
    );
  }

  return (
    <Container>
      <h2>Product Capability Restrictions</h2>
      <p className="text-muted">
        Assign logos and colors to product capabilities. Products with these logos/colors will only be visible to users with the corresponding product capability.
      </p>
      
      <Table striped bordered hover>
        <thead>
          <tr>
            <th>Capability</th>
            <th>Restricted Logos</th>
            <th>Restricted Colors</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {capabilities.map(cap => (
            <tr key={cap.id}>
              <td>{cap.capabilityName}</td>
              <td>
                <Badge bg="secondary">{cap.capability_logos?.length || 0}</Badge>
              </td>
              <td>
                <Badge bg="secondary">{cap.capability_colors?.length || 0}</Badge>
              </td>
              <td>
                <Button variant="outline-primary" size="sm" onClick={() => openModal(cap)}>
                  <Pencil /> Edit Restrictions
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <Modal show={showModal} onHide={closeModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            Edit Restrictions for: {selectedCapability?.capabilityName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <h5>Logos</h5>
          <p className="text-muted small">
            Products with selected logos will only be visible to users with this capability.
          </p>
          <div style={{ maxHeight: 200, overflowY: 'auto', marginBottom: 20 }}>
            {logos.map(logo => (
              <Form.Check
                key={logo.id}
                type="checkbox"
                id={`logo-${logo.id}`}
                label={
                  <span>
                    {logo.imageUrl && (
                      <img src={logo.imageUrl} alt="" style={{ height: 20, marginRight: 8 }} />
                    )}
                    {logo.decorationName || logo.code}
                  </span>
                }
                checked={capabilityLogos.includes(logo.id)}
                onChange={() => toggleLogo(logo.id)}
                disabled={saving}
              />
            ))}
            {logos.length === 0 && <p className="text-muted">No logos available</p>}
          </div>

          <hr />

          <h5>Colors</h5>
          <p className="text-muted small">
            Products in selected colors will only be visible to users with this capability.
          </p>
          <div style={{ maxHeight: 200, overflowY: 'auto' }}>
            {colors.map(color => (
              <Form.Check
                key={color.id}
                type="checkbox"
                id={`color-${color.id}`}
                label={
                  <span>
                    {color.hexColor && (
                      <span
                        style={{
                          display: 'inline-block',
                          width: 16,
                          height: 16,
                          backgroundColor: color.hexColor,
                          marginRight: 8,
                          border: '1px solid #ccc'
                        }}
                      />
                    )}
                    {color.colorName}
                  </span>
                }
                checked={capabilityColors.includes(color.id)}
                onChange={() => toggleColor(color.id)}
                disabled={saving}
              />
            ))}
            {colors.length === 0 && <p className="text-muted">No colors available</p>}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AdminCapabilities;

